import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, RefreshCw, Loader2, CheckCircle2, AlertTriangle, Sparkles } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { propsFundoModal } from '../../src/lib/backdropModal';

// Modal de "Sincronizar status sem gerar boleto" — para faturas cuja
// renegociação real já foi feita por fora do sistema. Não cria boleto, não
// cria parcelamento fictício: só abre um pedido de troca de status que
// precisa de aprovação de gerente/adm antes de qualquer coisa mudar em
// `faturas`. Ver docs/superpowers/specs/2026-09-02-sincronizacao-status-fatura-design.md
// e o incidente que motivou (régua de cobrança disparando por boleto de
// centavos criado só pra trocar status).

interface SincronizarStatusModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    preSelectedFatura?: any;
}

interface FaturaPendente {
    id: string;
    uc_codigo: string;
    cliente_nome: string;
    mes_referencia: string;
    valor_boleto_vigor: number;
    vencimento_boleto: string | null;
    status_boleto_vigor: string;
    num_cobranca_boleto?: string | null;
}

const formatDateBR = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    const [ano, mes, dia] = dateStr.split('T')[0].split('-');
    return `${dia}/${mes}/${ano}`;
};

const formatCurrency = (v: number) =>
    (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const normalizeUcLocal = (uc: any): string => {
    if (typeof uc !== 'string' && typeof uc !== 'number') return '';
    return String(uc).replace(/[.\-\s]/g, '').replace(/^0+/, '');
};

const MOTIVO_MIN_LENGTH = 8;
const VICK_DEBOUNCE_MS = 900;

type VeredictoVick = { fazSentido: boolean; comentario: string } | null;

const SincronizarStatusModal: React.FC<SincronizarStatusModalProps> = ({
    isOpen, onClose, onSuccess, preSelectedFatura
}) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();

    const [loading, setLoading] = useState(false);

    const [faturaSearchTerm, setFaturaSearchTerm] = useState('');
    const [faturasPendentes, setFaturasPendentes] = useState<FaturaPendente[]>([]);
    const [loadingFaturas, setLoadingFaturas] = useState(false);
    const [selectedFaturaIds, setSelectedFaturaIds] = useState<Set<string>>(new Set());
    const [selectedFaturasData, setSelectedFaturasData] = useState<Map<string, FaturaPendente>>(new Map());

    const [motivo, setMotivo] = useState('');
    const [vickChecking, setVickChecking] = useState(false);
    const [vickVeredicto, setVickVeredicto] = useState<VeredictoVick>(null);
    const vickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const vickRequestId = useRef(0);

    useEffect(() => {
        if (!isOpen) return;
        setFaturaSearchTerm('');
        setFaturasPendentes([]);
        setSelectedFaturaIds(new Set());
        setSelectedFaturasData(new Map());
        setMotivo('');
        setVickVeredicto(null);
        setVickChecking(false);

        if (preSelectedFatura) {
            const seed: FaturaPendente = {
                id: String(preSelectedFatura.id),
                uc_codigo: preSelectedFatura.uc_codigo || '',
                cliente_nome: preSelectedFatura.cliente_nome || '',
                mes_referencia: preSelectedFatura.mes_referencia || '',
                valor_boleto_vigor: Number(preSelectedFatura.valor_boleto_vigor) || 0,
                vencimento_boleto: preSelectedFatura.vencimento_boleto || null,
                status_boleto_vigor: preSelectedFatura.status_boleto_vigor || '',
                // Sem isto o aviso de "boleto Sicoob ainda vivo" (faturasComBoleto,
                // abaixo) nunca aparecia para quem abre o modal direto pelo menu "..."
                // da linha na grade — o caminho de entrada mais comum da feature —
                // porque só a busca preenchia este campo. CriarParcelamentoModal e
                // QuitarFaturasModal já copiam este campo no seed pelo mesmo motivo.
                num_cobranca_boleto: preSelectedFatura.num_cobranca_boleto || null,
            };
            setSelectedFaturaIds(new Set([seed.id]));
            setSelectedFaturasData(new Map([[seed.id, seed]]));
            setFaturaSearchTerm(preSelectedFatura.cliente_nome || preSelectedFatura.uc_codigo || '');
        }
    }, [isOpen]);

    // Busca por nome/UC — mesmo padrão de CriarParcelamentoModal.tsx
    useEffect(() => {
        const termo = faturaSearchTerm.trim();
        if (termo.length < 3) { setFaturasPendentes([]); return; }
        const fetchFaturas = async () => {
            setLoadingFaturas(true);
            try {
                const termoUc = normalizeUcLocal(termo);
                const orFilter = termoUc
                    ? `cliente_nome.ilike.%${termo}%,uc_codigo.ilike.%${termoUc}%`
                    : `cliente_nome.ilike.%${termo}%`;
                const { data, error } = await supabase
                    .from('faturas')
                    .select('id, uc_codigo, cliente_nome, mes_referencia, valor_boleto_vigor, vencimento_boleto, status_boleto_vigor, num_cobranca_boleto')
                    .or(orFilter)
                    .in('status_boleto_vigor', ['Em aberto', 'Vencido'])
                    // Exclui parcelas de acordos ativos (CriarParcelamentoModal.tsx
                    // segue a mesma regra) — sem isso, a parcela 2/5 de um parcelamento
                    // real em dia aparece igual a uma fatura solta vencida, e dá pra
                    // "sincronizar" por engano uma cobrança que devia continuar ativa.
                    .is('parcelamento_origem_id', null)
                    .order('vencimento_boleto', { ascending: true })
                    .limit(50);
                if (error) throw error;
                setFaturasPendentes((data || []) as FaturaPendente[]);
            } catch (err) {
                console.error('Erro ao buscar faturas:', err);
            } finally {
                setLoadingFaturas(false);
            }
        };
        const t = setTimeout(fetchFaturas, 400);
        return () => clearTimeout(t);
    }, [faturaSearchTerm]);

    const toggleFatura = (fatura: FaturaPendente) => {
        const id = String(fatura.id);
        setSelectedFaturaIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
        setSelectedFaturasData(prev => {
            const next = new Map(prev);
            if (next.has(id)) next.delete(id); else next.set(id, fatura);
            return next;
        });
    };

    const totalSelecionadas = Array.from(selectedFaturasData.values());
    const totalValor = totalSelecionadas.reduce((s, f) => s + (Number(f.valor_boleto_vigor) || 0), 0);
    // Sinaliza faturas com boleto Sicoob ainda vivo: sincronizar o status não
    // cancela esse boleto (diferente do CriarParcelamentoModal, que cancela) —
    // sem o aviso, o boleto antigo continua cobrável ao lado da cobrança nova
    // feita por fora, com risco de pagamento em duplicidade.
    const faturasComBoleto = totalSelecionadas.filter(f => !!f.num_cobranca_boleto);

    // Checagem leve da Vick — debounced, best-effort. Nunca bloqueia o envio:
    // se falhar ou a API estiver fora do ar, segue sem selo (indisponivel).
    useEffect(() => {
        if (vickTimer.current) clearTimeout(vickTimer.current);
        setVickVeredicto(null);
        const texto = motivo.trim();
        if (texto.length < MOTIVO_MIN_LENGTH || totalSelecionadas.length === 0) {
            setVickChecking(false);
            return;
        }
        const meuId = ++vickRequestId.current;
        vickTimer.current = setTimeout(async () => {
            setVickChecking(true);
            try {
                const { data, error } = await supabase.functions.invoke('vick-valida-sync-status', {
                    body: {
                        motivo: texto,
                        faturas: totalSelecionadas.map(f => ({ mesReferencia: f.mes_referencia, valor: f.valor_boleto_vigor })),
                    },
                });
                if (meuId !== vickRequestId.current) return; // motivo mudou de novo, descarta resposta velha
                if (error || !data?.ok || data?.indisponivel || typeof data?.fazSentido !== 'boolean') {
                    setVickVeredicto(null);
                } else {
                    setVickVeredicto({ fazSentido: data.fazSentido, comentario: data.comentario || '' });
                }
            } catch (err) {
                console.error('Vick indisponível para checagem (segue sem selo):', err);
                if (meuId === vickRequestId.current) setVickVeredicto(null);
            } finally {
                if (meuId === vickRequestId.current) setVickChecking(false);
            }
        }, VICK_DEBOUNCE_MS);
        return () => { if (vickTimer.current) clearTimeout(vickTimer.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [motivo, selectedFaturaIds.size]);

    const podeEnviar = motivo.trim().length >= MOTIVO_MIN_LENGTH && totalSelecionadas.length > 0 && !vickChecking;

    const handleSubmit = async () => {
        if (!podeEnviar) return;
        setLoading(true);
        try {
            const motivoFinal = motivo.trim();
            const linhas = totalSelecionadas.map(f => ({
                fatura_id: Number(f.id),
                status_atual: f.status_boleto_vigor,
                status_alvo: 'Parcelado',
                motivo: motivoFinal,
                solicitado_por_id: user?.id,
                solicitado_por_nome: profile?.full_name || user?.email || 'Usuário',
                vick_faz_sentido: vickVeredicto?.fazSentido ?? null,
                vick_comentario: vickVeredicto?.comentario ?? null,
            }));

            const { error } = await supabase.from('fatura_status_sync_requests').insert(linhas);
            if (error) throw error;

            // Notifica gerentes/adm — mesmo padrão de checkAndRequestDiscountApproval
            // (src/lib/discountApproval.ts): um insert por destinatário.
            const { data: aprovadores } = await supabase
                .from('profiles')
                .select('id')
                .in('role', ['gerente', 'adm']);
            if (aprovadores && aprovadores.length > 0) {
                const resumo = totalSelecionadas.length === 1
                    ? totalSelecionadas[0].cliente_nome
                    : `${totalSelecionadas.length} faturas de ${totalSelecionadas[0]?.cliente_nome || 'cliente'}`;
                for (const a of aprovadores) {
                    await supabase.from('notifications').insert({
                        user_id: a.id,
                        type: 'fatura_status_sync_pendente',
                        title: 'Sincronização de status pendente',
                        content: `${profile?.full_name || 'Alguém'} pediu para sincronizar o status de ${resumo} sem gerar boleto.`,
                        link: '/financeiro/sincronizacao-status',
                        read: false,
                    });
                }
            }

            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'sincronizar_status', acao: 'solicitar', origem: 'acao' });
            await logAction(user?.id || 'SYSTEM', 'REQUEST_FATURA_STATUS_SYNC', {
                fatura_ids: totalSelecionadas.map(f => f.id),
                motivo: motivoFinal,
                vick_faz_sentido: vickVeredicto?.fazSentido ?? null,
            });

            // Diz ONDE acompanhar: o modal fecha e a fatura continua "Vencido" na
            // grade até um gerente aprovar — sem essa pista, o analista não tem como
            // saber que o pedido existe e pode reenviar achando que falhou.
            addToast(
                `Pedido enviado — ${totalSelecionadas.length} fatura(s) aguardando aprovação de um gerente. Acompanhe em Financeiro › Sincronização de Status.`,
                'success',
                10000
            );
            if (onSuccess) onSuccess();
            onClose();
        } catch (error: any) {
            console.error(error);
            addToast(`Erro ao enviar pedido: ${error.message || error}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" style={{ zIndex: 9999 }} {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-gray-800 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-fade-in" onClick={e => e.stopPropagation()}>

                <div className="px-6 py-5 bg-gradient-to-r from-primary/10 to-transparent border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-primary/10 rounded-xl text-primary">
                            <RefreshCw size={22} />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Sincronizar status sem gerar boleto</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                A Vick dá uma lida no motivo, depois vai para aprovação de um gerente. Nada muda antes disso.
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Fechar" className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                            <Search size={16} className="text-primary" />
                            Buscar faturas vencidas / em aberto
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Nome do cliente ou código da UC..."
                                value={faturaSearchTerm}
                                onChange={(e) => setFaturaSearchTerm(e.target.value)}
                                className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all pl-11"
                                autoFocus={!preSelectedFatura}
                            />
                            <Search className="absolute left-4 top-3.5 text-gray-400" size={18} />
                            {loadingFaturas && <Loader2 size={16} className="absolute right-4 top-3.5 animate-spin text-primary" />}
                        </div>
                    </div>

                    {faturaSearchTerm.trim().length >= 3 && !loadingFaturas && faturasPendentes.length === 0 && (
                        <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-700 text-sm text-gray-500 text-center">
                            Nenhuma fatura em aberto ou vencida encontrada para "{faturaSearchTerm}".
                        </div>
                    )}

                    {(faturasPendentes.length > 0 || selectedFaturasData.size > 0) && (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50 max-h-56 overflow-y-auto">
                            {[...faturasPendentes, ...Array.from(selectedFaturasData.values()).filter(f => !faturasPendentes.some(fp => String(fp.id) === String(f.id)))]
                                .map(fatura => {
                                    const isSelected = selectedFaturaIds.has(String(fatura.id));
                                    return (
                                        <label
                                            key={fatura.id}
                                            className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${isSelected ? 'bg-primary/5' : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}
                                        >
                                            <input type="checkbox" checked={isSelected} onChange={() => toggleFatura(fatura)} className="w-4 h-4 accent-primary rounded" />
                                            <div className="flex-1 flex items-center justify-between gap-2 min-w-0">
                                                <div className="min-w-0">
                                                    <span className="text-sm font-bold text-gray-900 dark:text-white">{fatura.mes_referencia}</span>
                                                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">Venc. {formatDateBR(fatura.vencimento_boleto)}</span>
                                                    <p className="text-[11px] text-gray-400 truncate">UC {fatura.uc_codigo?.trim()} • {fatura.cliente_nome}</p>
                                                </div>
                                                <span className="text-sm font-bold text-gray-900 dark:text-white shrink-0">{formatCurrency(fatura.valor_boleto_vigor)}</span>
                                            </div>
                                        </label>
                                    );
                                })}
                            {selectedFaturasData.size > 0 && (
                                <div className="px-4 py-2.5 bg-primary/5 flex items-center justify-between sticky bottom-0">
                                    <span className="text-xs font-semibold text-primary">{selectedFaturasData.size} fatura(s) selecionada(s)</span>
                                    <span className="text-sm font-bold text-primary">Total: {formatCurrency(totalValor)}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {faturasComBoleto.length > 0 && (
                        <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                            <AlertTriangle size={15} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-700 dark:text-amber-300 font-medium">
                                {faturasComBoleto.length} fatura(s) ainda têm boleto emitido no Sicoob. Sincronizar o status <strong>não cancela</strong> esse boleto — se a cobrança já foi feita por fora, cancele-o manualmente pra evitar pagamento em duplicidade.
                            </p>
                        </div>
                    )}

                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Motivo da sincronização</label>
                        <textarea
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                            rows={3}
                            placeholder="Ex.: débito já renegociado por fora com o cliente — cobrado como boleto avulso nas faturas de MAI a JUL/2026."
                            className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-none"
                        />
                        <p className="text-[11px] text-gray-400">Obrigatório, mínimo de {MOTIVO_MIN_LENGTH} caracteres — é o que o gerente vê antes de aprovar.</p>

                        {(vickChecking || vickVeredicto) && (
                            <div className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border text-xs ${
                                vickChecking ? 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400' :
                                vickVeredicto?.fazSentido ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300' :
                                'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300'
                            }`}>
                                {vickChecking ? <Loader2 size={14} className="animate-spin shrink-0 mt-0.5" /> :
                                 vickVeredicto?.fazSentido ? <CheckCircle2 size={14} className="shrink-0 mt-0.5" /> :
                                 <AlertTriangle size={14} className="shrink-0 mt-0.5" />}
                                <span>
                                    <span className="font-bold flex items-center gap-1">
                                        <Sparkles size={11} /> {vickChecking ? 'Vick está lendo a justificativa…' : vickVeredicto?.fazSentido ? 'Vick: faz sentido' : 'Vick: parece vago'}
                                    </span>
                                    {!vickChecking && vickVeredicto && <span className="block mt-0.5 opacity-90">{vickVeredicto.comentario}</span>}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-6 py-5 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
                    <button onClick={onClose} className="px-4 py-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-750 font-semibold rounded-xl transition-colors">
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!podeEnviar || loading}
                        className="px-6 py-2.5 bg-primary text-white font-bold rounded-xl shadow-lg shadow-primary/30 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2 disabled:opacity-50 disabled:hover:scale-100"
                    >
                        {loading ? <><Loader2 className="animate-spin" size={18} /> Enviando...</> :
                         vickVeredicto && !vickVeredicto.fazSentido ? <>Enviar mesmo assim</> :
                         <>Enviar para aprovação</>}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SincronizarStatusModal;
